import React from "react";
import { FaCheck, FaPlay, FaLock } from "react-icons/fa6";
// iOS uses ONE shared course-art thumbnail for every chapter card
// (client-native ChapterCard.tsx:20 `HORIZONTAL_THUMB`), not the per-video
// YouTube frame — the Chapter DTO has no per-chapter image field. Mirror that
// so the web path matches iOS (same art on every card).
import courseThumb from "../../assets/course-thumb-horizontal.png";

/**
 * Presentational image-backed chapter/module card shared by the regular course
 * path (ChapterCard, Chapter DTO adapter) and the Flight School home path
 * (FlightSchoolHomePath, module adapter) — the single iOS ChapterCard
 * (client-native/src/features/home/components/ChapterCard.tsx:61-209) backs both.
 *
 * Mirrors iOS IN_PROGRESS / COMPLETED / LOCKED gradients (ChapterCard.tsx:27-41):
 * a darkening top→bottom overlay so the white title stays legible on the thumb.
 */

const IN_PROGRESS_GRADIENT =
  "linear-gradient(to bottom, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%)";
const COMPLETED_GRADIENT =
  "linear-gradient(to bottom, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.9) 100%)";
const LOCKED_GRADIENT =
  "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.75) 40%, rgba(0,0,0,0.92) 100%)";

export type ChapterCardStatus = "completed" | "current" | "locked";

const StatusChapterCard: React.FC<{
  status: ChapterCardStatus;
  title: string;
  subtitle?: string;
  onPress: () => void;
}> = ({ status, title, subtitle, onPress }) => {
  const isCompleted = status === "completed";
  const isCurrent = status === "current";

  const gradient = isCurrent
    ? IN_PROGRESS_GRADIENT
    : isCompleted
      ? COMPLETED_GRADIENT
      : LOCKED_GRADIENT;

  const eyebrow = isCompleted
    ? "COMPLETED"
    : isCurrent
      ? "IN PROGRESS"
      : "UP NEXT";

  return (
    <button
      type="button"
      onClick={onPress}
      className="relative block w-full h-[120px] rounded-2xl overflow-hidden text-left shadow-md transition-opacity active:opacity-80 bg-[var(--color-grey-lightest)]"
    >
      {/* Shared course-art thumbnail — same asset on every card, matching iOS. */}
      <img
        src={courseThumb}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Darkening overlay per status */}
      <div className="absolute inset-0" style={{ background: gradient }} />

      {/* Center status icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        {isCompleted ? (
          <div className="w-[33px] h-[33px] rounded-full bg-[var(--color-green)] flex items-center justify-center">
            <FaCheck className="w-5 h-5 text-white" />
          </div>
        ) : isCurrent ? (
          <FaPlay className="w-7 h-7 text-white ml-1" />
        ) : (
          <FaLock className="w-6 h-6 text-white" />
        )}
      </div>

      {/* Bottom text */}
      <div className="absolute inset-x-0 bottom-0 px-3.5 pb-3 pt-1.5">
        <div
          className="text-[10px] font-semibold text-white/80"
          style={{ letterSpacing: "1.2px" }}
        >
          {eyebrow}
        </div>
        <div className="text-base font-bold text-white truncate">{title}</div>
        {subtitle && (
          <div className="text-[11px] text-white/70 truncate mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
    </button>
  );
};

export default StatusChapterCard;
